const QUEUE_KEY = 'ead_offline_event_queue';

// Tipos de evento da fila offline — centralizados pra evitar typo silencioso.
export const OfflineEventType = Object.freeze({
  ENROLL_PROGRESS: 'enrollProgress',
  OFFLINE_SESSION: 'offlineSession'
});

function load () {
  try {
    return JSON.parse(localStorage.getItem(QUEUE_KEY) || '[]');
  } catch {
    return [];
  }
}

function save (queue) {
  try {
    localStorage.setItem(QUEUE_KEY, JSON.stringify(queue));
  } catch (e) {
    console.warn('[offlineEventQueue] Falha ao salvar fila:', e);
  }
}

// id único entre relançamentos do app: crypto.randomUUID quando disponível, senão um
// contador PERSISTIDO (um seq em RAM resetava a cada launch → 2 enqueues no mesmo ms de
// launches distintos colidiam, e remove()/recordFailures por id afetariam os dois).
function nextId () {
  try {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  } catch { /* sem crypto.randomUUID → fallback abaixo */ }
  let n = parseInt(localStorage.getItem('ead_offline_seq') || '0', 10) || 0;
  n += 1;
  try { localStorage.setItem('ead_offline_seq', String(n)); } catch { /* storage cheio */ }
  return `${Date.now()}_${n}`;
}

export const offlineEventQueue = {
  // Enfileira um evento. Dedup por aula (learningObjectId): mantém só o evento
  // mais recente do mesmo LO — o backend só guarda `complete`, então o último
  // estado é o que vale, e evita acumular progressos redundantes na fila.
  // `studentId` carimba o DONO do evento. A fila vive no localStorage e sobrevive
  // ao logout: num device compartilhado (família/escola), sem isto os eventos do
  // aluno A seriam enviados depois pelo aluno B — no caso do progresso, com o JWT
  // de B, marcando aulas de A como concluídas. O flush filtra por este campo.
  enqueue (type, payload, studentId) {
    const loId = payload && payload.learningObjectId;
    const queue = loId != null
      ? load().filter(e => !(e.type === type && e.payload && e.payload.learningObjectId === loId))
      : load();
    queue.push({ id: nextId(), type, payload, studentId: studentId != null ? String(studentId) : null });
    save(queue);
  },

  // Eventos que pertencem ao aluno informado.
  //
  // Evento SEM carimbo ("órfão") acontece de duas formas: enfileirado antes deste
  // campo existir, ou enfileirado com o user store ainda não hidratado (boot
  // offline). Antes eles eram tratados como do usuário atual, o que num device
  // compartilhado enviava o progresso do aluno A sob o JWT do B — o servidor
  // grava por CodMatricula e sobrescrevia o progresso de A.
  //
  // Descartá-los também não serve: seria perder progresso legítimo já capturado.
  // Então a regra é PROVAR: `canClaimOrphan` recebe o evento e responde se o
  // payload dele identifica o aluno logado (matrícula na lista dele, e-mail que
  // bate). Sem prova, o evento FICA na fila e sobe quando o dono verdadeiro
  // logar. Nunca é enviado sob outra conta, nunca é apagado.
  ownedBy (studentId, canClaimOrphan) {
    const sid = studentId != null ? String(studentId) : null;
    return this.peekAll().filter(e => {
      if (e.studentId != null) return e.studentId === sid;
      return typeof canClaimOrphan === 'function' ? !!canClaimOrphan(e) : false;
    });
  },

  // Lê a fila SEM remover (peek). A remoção é por item, só após o reenvio dar
  // certo (remove-on-success) — ver enroll/flushOfflineEventQueue. Faz backfill
  // de `id` em itens legados (gravados antes deste esquema) pra que `remove`
  // funcione neles também.
  peekAll () {
    const queue = load();
    let changed = false;
    queue.forEach(e => {
      if (!e.id) { e.id = nextId(); changed = true; }
    });
    if (changed) save(queue);
    return queue;
  },

  // Remove um evento pelo id (após o reenvio confirmar).
  remove (id) {
    save(load().filter(e => e.id !== id));
  },

  // Remove vários ids num único load/save — evita o O(n²) do remove() em laço no flush.
  removeMany (ids) {
    const set = new Set(ids);
    save(load().filter(e => !set.has(e.id)));
  },

  // Conta uma tentativa falha nos ids dados e descarta (poison) os que atingirem
  // maxAttempts. Retorna os ids descartados. Chamar só em falha de RESPOSTA HTTP
  // (4xx/5xx) — falha de rede/transporte é transitória e não deve queimar tentativa.
  recordFailures (ids, maxAttempts) {
    const set = new Set(ids);
    const dropped = [];
    const kept = load().filter(e => {
      if (!set.has(e.id)) return true;
      e.attempts = (e.attempts || 0) + 1;
      if (e.attempts >= maxAttempts) { dropped.push(e.id); return false; }
      return true;
    });
    save(kept);
    return dropped;
  }
};
